import {
  toIdbParameterRows,
  toMessageRows
} from 'Utils/applyVersionedParameters';

export const DB_NAME = 'bilyoner-resources';
export const DB_VERSION = 7;
export const MESSAGES_STORE = 'messages';
export const TJK_STORE = 'tjk-messages';
const RETIRED_STORES = ['parameters'];

const ensureObjectStores = db => {
  if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
    db.createObjectStore(MESSAGES_STORE, { keyPath: 'key' });
  }
  if (!db.objectStoreNames.contains(TJK_STORE)) {
    db.createObjectStore(TJK_STORE, { keyPath: 'key' });
  }
  RETIRED_STORES.forEach(storeName => {
    if (db.objectStoreNames.contains(storeName)) {
      db.deleteObjectStore(storeName);
    }
  });
};

const hasIndexedDb = () => typeof indexedDB !== 'undefined';

const attachVersionChange = db => {
  db.onversionchange = () => {
    db.close();
  };
};

const openDb = () => {
  if (!hasIndexedDb()) {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    let settled = false;

    const fail = err => {
      if (settled) {
        return;
      }
      settled = true;
      reject(err);
    };

    request.onupgradeneeded = event => {
      const db = event.target.result;
      attachVersionChange(db);
      ensureObjectStores(db);
    };

    request.onsuccess = () => {
      const db = request.result;
      attachVersionChange(db);
      if (settled) {
        db.close();
        return;
      }
      settled = true;
      resolve(db);
    };

    request.onerror = () => fail(request.error);
    request.onblocked = () => {
      console.warn('[environmentDb] IndexedDB open blocked');
      fail(new Error('[environmentDb] IndexedDB open blocked by another connection'));
    };
  });
};

const withDb = async fn => {
  const db = await openDb();
  try {
    return await fn(db);
  } finally {
    db.close();
  }
};

export const readStore = async storeName => {
  if (!hasIndexedDb()) {
    return [];
  }

  try {
    return await withDb(db => new Promise((resolve, reject) => {
      if (!db.objectStoreNames.contains(storeName)) {
        resolve([]);
        return;
      }

      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.getAll();

      tx.oncomplete = () => resolve(request.result || []);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error(`IndexedDB abort (${storeName})`));
      request.onerror = () => reject(request.error);
    }));
  } catch (e) {
    console.error(`[environmentDb] read failed (${storeName}):`, e);
    return [];
  }
};

const runReadWrite = (db, storeName, work) => new Promise((resolve, reject) => {
  if (!db.objectStoreNames.contains(storeName)) {
    resolve();
    return;
  }

  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);

  tx.oncomplete = () => resolve();
  tx.onerror = () => reject(tx.error);
  tx.onabort = () => reject(tx.error || new Error(`IndexedDB abort (${storeName})`));

  work(store);
});

export const writeStore = async (storeName, items) => {
  if (!hasIndexedDb()) {
    return;
  }

  const valid = (items || []).filter(item => item && item.key != null && item.key !== '');
  if (!valid.length) {
    return;
  }

  const db = await openDb();

  try {
    // clear + all puts in one transaction: IndexedDB serializes transactions on the same
    // store, so a concurrent writeStore call can never see a clear() from this call
    // interleaved with its own puts (which happened when clear and each chunk were separate
    // transactions, and dropped rows under concurrent writers).
    await runReadWrite(db, storeName, store => {
      store.clear();
      valid.forEach(item => store.put(item));
    });
  } finally {
    db.close();
  }
};

export const readParameterRows = async () => toMessageRows(await readStore(MESSAGES_STORE));

export const writeParameterRows = async rows => {
  const idbRows = toIdbParameterRows(rows);
  if (!idbRows.length) {
    return;
  }

  await writeStore(MESSAGES_STORE, idbRows);
};
